import "./dashboard.component.scss";
import { useEffect, useRef, useState } from "react";
import SEARCH from "../../assets/images/search.svg";
import FILTER from "../../assets/images/filter.svg";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import UPLOAD_ICON from "../../assets/images/upload.svg";
import ICON_DATABASE from "../../assets/images/database.svg";
import WARNING_ICON from "../../assets/images/warning.svg";
import NOT_FOUND_ICON from "../../assets/images/not-found.svg";
import UploadVideoModal from "../../modal/upload-video/upload-video";
import DashboardTableWithPagination from "./table/dashboard-table.component";
import Button from '@mui/material/Button';
import { FormCheck } from "react-bootstrap";
import MediaService from "../../services/media.service";
import CircularProgress from '@mui/material/CircularProgress';
import { Media } from "../../model/media.model";
import { useSnackbar } from "../../utility/snackbar.utility";
import CustomSnackbar from "../snackbar/snackbar.component";
import LinearProgress from '@mui/material/LinearProgress';
import { MediaUsage } from "../../model/media-usage.model";
import { mediaPageList } from "../../config/media.config";
import { useAuth } from "../../utility/use-auth";
import { getUsers } from "../../services/api.service";
import SearchUploadByModal from "../../modal/search-upload-by/search-upload-by";
import { usePaginationFilter } from "../../utility/pagination-filter";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from "@mui/material/MenuItem";
import FormControl from '@mui/material/FormControl';
import { UserRole } from "../../model/user-role.enum";

const DashboardComponent = () => {

    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
    const { user } = useAuth();
    const {
        pagination, setPagination,
        filters, setFilters,
        searchTopic, setSearchTopic,
        lastId, setLastId,
        isArchived, setIsArchived,
        limitItem, setLimitItem,
    } = usePaginationFilter();

    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [mediaList, setMediaList] = useState<Media[]>([]);
    const [usage, setUsage] = useState<MediaUsage>();
    const [isShowUploadModal, setIsShowUploadModal] = useState(false);
    const [isShowSearchModal, setIsShowSearchModal] = useState(false);
    const [userList, setUserList] = useState<string[]>([]);
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [skipFetch, setSkipFetch] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        initializeData();
    }, []);

    useEffect(() => {
        if (isInitialLoad) {
            setIsInitialLoad(false);
            return;
        }

        if (!skipFetch) {
            fetchMedia();
        }
        setSkipFetch(false);
    }, [filters, isArchived, lastId, limitItem]);

    useEffect(() => {
        const idList = mediaList
            .filter(item => !item.is_transcript_available && !item.transcript_status?.startsWith("ERROR"))
            .map(item => item.id);

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (idList.length > 0) {
            intervalRef.current = setInterval(() => {
                fetchStatus(idList);
            }, 60000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [mediaList]);

    const initializeData = async () => {
        await Promise.all([fetchUsers(), fetchMedia(), fetchUsage()]);
    };

    const fetchMedia = async (isClearFilter = false) => {
        try {
            setIsLoading(true);

            if (hasActiveFilters() && (!isClearFilter || isArchived)) {
                setIsFilterActive(true);
            } else {
                setIsFilterActive(false);
            }

            const mediaResponse = isClearFilter
                ? await MediaService.getMedia(limitItem, "", "", [], "", "", isArchived)
                : await MediaService.getMedia(
                    limitItem,
                    lastId,
                    searchTopic,
                    filters.selectedUploadBy || [],
                    formatDate(filters.selectedStartDate),
                    formatDate(filters.selectedEndDate, true),
                    isArchived,
                    determineInfoState(),
                );

            setTotalItems(mediaResponse.data.total_items);
            setMediaList(mediaResponse.data.media);
            setPagination((prev) => ({
                ...prev,
                totalPage: Math.ceil(mediaResponse.data.total_items / limitItem),
            }));
            const lastIndexId = mediaResponse.data?.media[mediaResponse.data.media.length - 1]?.id;
            updateLastIdList(lastIndexId, pagination.page);
        } catch {
            showSnackbar("", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const updateLastIdList = (lastId: string, currentPage: number) => {
        if (!lastId) return;

        setPagination((prev) => {
            const targetPage = currentPage + 1;
            let found = false;

            const updatedLastIdList = prev.lastIdList.map(item => {
                if (item.page === targetPage) {
                    found = true;
                    return { ...item, lastId };
                }
                return item;
            });

            if (!found) {
                updatedLastIdList.push({ page: targetPage, lastId });
            }

            return { ...prev, lastIdList: updatedLastIdList };
        });
    };

    const fetchUsage = async () => {
        try {
            const response = await MediaService.getUsage();
            setUsage(response.data);
        } catch {
            showSnackbar("", "error");
        }
    };

    const fetchStatus = async (intervalIds: string[]) => {
        try {
            const response = await MediaService.getStatus(intervalIds);
            const data = response.data;

            const idList = data
                .filter(item => !item.is_transcript_available && !item.transcript_status?.startsWith("ERROR"))
                .map(item => item.id);

            setMediaList(prev =>
                prev.map(item => {
                    const updatedItem = data.find(d => d.id === item.id);
                    return updatedItem ? { ...item, ...updatedItem } : item;
                })
            );

            if (idList.length === 0 && intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        } catch (error) {
            console.log('error', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await getUsers();
            if (res?.length > 0) {
                setUserList(
                    res.filter(item => item.email !== null).map(item => item.email as string)
                );
            }
        } catch {
            showSnackbar("", "error");
        }
    };

    const determineInfoState = () => {
        const { infographic, noInfographic } = filters.infographicStates;
        if ((infographic && noInfographic) || (!infographic && !noInfographic)) {
            return undefined;
        }
        return infographic;
    };

    const formatDate = (date: Date | null, endOfDay: boolean = false) => {
        if (!date) return "";

        if (endOfDay) {
            date.setHours(23, 59, 59, 999);
        } else {
            date.setHours(0, 0, 0, 0);
        }

        const tzOffset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - tzOffset).toISOString().slice(0, 19);
    };


    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTopic(e.target.value);
    };

    const handleDateChange = (e: Date | null, type: "start" | "end") => {
        setFilters((prev) => ({
            ...prev,
            [type === "start" ? "selectedStartDate" : "selectedEndDate"]: e,
        }));
        setDefaultPagination();
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { id, checked } = event.target;
        setFilters((prev) => ({
            ...prev,
            infographicStates: {
                ...prev.infographicStates,
                [id]: checked,
            },
        }));
        setDefaultPagination();
    };

    const handleUploadOpen = () => {
        fetchUsage();
        setIsShowUploadModal(true);
    }

    const handleUploadClose = (isShowSuccess: boolean) => {
        setIsShowUploadModal(false);
        if (isShowSuccess) {
            fetchMedia();
            showSnackbar("อัปโหลดวิดีโอเสร็จสิ้น AI กำลังสร้าง Transcript กรุณารอสักครู่", "success", 4000);
        }
    };

    const handlePaginationChange = (direction: "next" | "previous") => {
        setPagination((prev) => {
            const newPage = direction === "next" ? prev.page + 1 : Math.max(prev.page - 1, 1);
            const newLastId = direction === "next"
                ? mediaList[mediaList.length - 1]?.id
                : prev.lastIdList.find(item => item.page === newPage)?.lastId || '';

            setLastId(newLastId);
            return { ...prev, page: newPage };
        });
    };

    const handleClearFilter = () => {
        setDefaultFilters();
        setDefaultPagination();
        setSkipFetch(true);
        fetchMedia(true);
    };

    const handleCloseSearchModal = (result) => {
        setIsShowSearchModal(false);
        if (!result) return;

        setFilters((prev) => ({ ...prev, selectedUploadBy: result }));
        setDefaultPagination();
    }

    const isUsageHigh = (total: number, max: number): boolean => max ? (total * 100) / max >= 95 : false;

    const hasActiveFilters = () => {
        const { selectedStartDate, selectedEndDate, selectedUploadBy } = filters;
        const { infographic, noInfographic } = filters.infographicStates;

        return Boolean(
            isArchived ||
            searchTopic.trim() ||
            selectedStartDate ||
            selectedEndDate ||
            (selectedUploadBy && selectedUploadBy.length > 0) ||
            (!infographic || !noInfographic)
        );
    };

    const formatEmailList = (emailArray) => {
        if (emailArray.length < 2) {
            return emailArray[0];
        }

        const firstEmail = emailArray[0].slice(0, 10);
        const remainingCount = emailArray.length - 1;

        return `${firstEmail}... +${remainingCount}`;
    };

    const handleToggleMedia = (isArchived: boolean) => {
        setIsArchived(isArchived);
        setDefaultFilters();
        setDefaultPagination();
    };

    const setDefaultPagination = () => {
        setPagination({
            totalPage: 0,
            page: 1,
            lastIdList: [{ page: 1, lastId: "" }],
        });
        setLastId('');
    }

    const setDefaultFilters = () => {
        setSearchTopic('');
        setFilters({
            selectedStartDate: null,
            selectedEndDate: null,
            selectedUploadBy: [],
            infographicStates: {
                infographic: false,
                noInfographic: false,
            }
        });
    }

    const handleDeleteHideMedia = () => {
        const isLastItemOnPage = mediaList.length === 1;
        if (isLastItemOnPage) {
            const newPage = Math.max(pagination.page - 1, 1);
            setPagination(prev => ({ ...prev, page: newPage }));

            const lastId = pagination.lastIdList.find(item => item.page === newPage)?.lastId || '';
            setLastId(lastId);

            if (newPage === 1) {
                fetchMedia();
            }
        } else {
            fetchMedia();
        }
    };

    const handleSelectPageChange = (event: SelectChangeEvent) => {
        setLimitItem(Number(event.target.value));
        setDefaultPagination();
    };

    return (
        <>
            <div className="dashboard-control">
                <div className="dashboard-main-control">

                    <div className="dashboard-main-top-control">
                        <div>
                            {
                                isArchived ?
                                    <>
                                        <Button variant="text" className="button-back-step mb-2" startIcon={<KeyboardArrowLeftIcon />} onClick={() => handleToggleMedia(false)}>
                                            รายการทั้งหมด
                                        </Button>
                                        <p className="dashboard-main-top-text">รายการที่ถูกซ่อน</p>
                                    </> :
                                    <p className="dashboard-main-top-text">หน้าหลัก</p>
                            }
                        </div>

                        {
                            !isArchived &&
                            <div className="d-flex align-items-center">
                                {
                                    usage &&
                                    <div className="me-3">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <div className="d-flex align-items-center">
                                                <img src={ICON_DATABASE} className="me-1" width={12} height={12} />
                                                <span className="progress-title">จำนวนการใช้งาน</span>
                                            </div>

                                            <span className={`progress-value ${isUsageHigh(usage.total_usage_minutes, usage.max_limit_minutes) ? 'warning' : ''}`}>
                                                {
                                                    isUsageHigh(usage.total_usage_minutes, usage.max_limit_minutes) &&
                                                    <img src={WARNING_ICON} width={14} height={14} className="me-1" />
                                                }
                                                {usage?.total_usage_minutes}/{usage?.max_limit_minutes} นาที
                                            </span>
                                        </div>

                                        <LinearProgress
                                            variant="determinate"
                                            value={(usage?.total_usage_minutes * 100) / usage?.max_limit_minutes}
                                            className="progress-bar-control"
                                        />
                                    </div>
                                }

                                {
                                    usage &&
                                    <Button variant="contained" className="button-dashboard" onClick={handleUploadOpen}>
                                        อัปโหลดวิดีโอ/ไฟล์เสียง
                                    </Button>
                                }
                            </div>
                        }
                    </div>

                    <form onSubmit={(event) => { event.preventDefault(); fetchMedia(); }}>
                        <div className="dashboard-search-control">
                            <div className="dashboard-search-form-control">
                                <img src={SEARCH} className="dashboard-search-icon"></img>
                                <input
                                    className="dashboard-search-form"
                                    type="text"
                                    id="search"
                                    placeholder="ค้นหา (เช่น ชื่อหัวข้อ)"
                                    value={searchTopic}
                                    onChange={handleTitleChange}
                                />
                            </div>
                            <Button type="submit" variant="outlined" className="button-dashboard" onClick={setDefaultPagination}>ค้นหา</Button>
                        </div>
                    </form>

                    <div className="dashboard-filter-control">
                        <div className="d-flex">
                            <div className="dashboard-filter-form-control">
                                <img src={FILTER} className="dashboard-filter-icon"></img>
                                <div className="dashboard-filter-form">
                                    <DatePicker
                                        className="dashboard-date-form-control"
                                        selected={filters.selectedStartDate}
                                        onChange={(e) => handleDateChange(e, "start")}
                                        placeholderText="วันที่อัปโหลดตั้งแต่"
                                        dateFormat="dd/MM/yyyy"
                                        id="search-date"
                                        selectsStart
                                        startDate={filters.selectedStartDate}
                                        endDate={filters.selectedEndDate}
                                        maxDate={(filters.selectedEndDate) ? filters.selectedEndDate : undefined}
                                    />
                                    <FontAwesomeIcon icon={faChevronDown} className="dashboard-dropdown-form-icon"></FontAwesomeIcon>
                                </div>
                                <div className="dashboard-filter-form">
                                    <DatePicker
                                        className="dashboard-date-form-control"
                                        selected={filters.selectedEndDate}
                                        onChange={(e) => handleDateChange(e, "end")}
                                        placeholderText="วันที่อัปโหลดถึง"
                                        dateFormat="dd/MM/yyyy"
                                        id="search-date"
                                        selectsEnd
                                        startDate={filters.selectedStartDate}
                                        endDate={filters.selectedEndDate}
                                        minDate={(filters.selectedStartDate) ? filters.selectedStartDate : undefined}
                                    />
                                    <FontAwesomeIcon icon={faChevronDown} className="dashboard-dropdown-form-icon"></FontAwesomeIcon>
                                </div>
                                {
                                    (user?.role !== UserRole.STAFF && userList) &&
                                    <div className="dashboard-filter-form" onClick={() => setIsShowSearchModal(true)}>
                                        {
                                            Array.isArray(filters?.selectedUploadBy) && (filters.selectedUploadBy.length > 0) ?
                                                <span className="dashboard-filter-form-text">
                                                    {formatEmailList(filters.selectedUploadBy)}
                                                </span> :
                                                <span>ผู้อัปโหลด</span>
                                        }

                                        <FontAwesomeIcon icon={faChevronDown} className="dashboard-dropdown-form-icon"></FontAwesomeIcon>
                                    </div>
                                }
                            </div>

                            <div className="dashboard-filter-status-control">
                                <FormCheck
                                    className="custom-checkbox dashboard-filter-status-checkbox"
                                    type="checkbox"
                                    id="infographic"
                                    label="สร้าง Infographic แล้ว"
                                    checked={filters.infographicStates.infographic}
                                    onChange={handleCheckboxChange}
                                />

                                <FormCheck
                                    className="custom-checkbox dashboard-filter-status-checkbox"
                                    type="checkbox"
                                    id="noInfographic"
                                    label="ยังไม่สร้าง Infographic"
                                    checked={filters.infographicStates.noInfographic}
                                    onChange={handleCheckboxChange}
                                />

                                <Button variant="text" className="dashboard-filter-clear-all-text" onClick={handleClearFilter}>
                                    ลบตัวกรองทั้งหมด
                                </Button>

                                {
                                    !isArchived &&
                                    <Button variant="text" className="dashboard-filter-clear-all-text ms-3" onClick={() => handleToggleMedia(true)}>
                                        รายการที่ถูกซ่อน
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-content-control">
                    <div className="dashboard-content-item-control">
                        {
                            isLoading ?
                                <div className="d-flex justify-content-center pt-5">
                                    <CircularProgress />
                                </div> :
                                (isFilterActive && totalItems === 0) ?
                                    <div className="dashboard-content-empty-control">
                                        <img src={NOT_FOUND_ICON} width={70} height={70} />
                                        <span className="dashboard-content-empty-title not-found">ไม่พบรายการที่คุณค้นหา</span>
                                    </div> :
                                    (totalItems > 0) ?
                                        <div className="d-flex flex-column justify-content-between w-100 h-100">
                                            <DashboardTableWithPagination data={mediaList} page={pagination.page} onUpdate={handleDeleteHideMedia}></DashboardTableWithPagination>

                                            <div className="d-flex justify-content-end align-items-center">
                                                <div className="dashboard-content-select-wrapper">
                                                    <span>แสดงหน้าละ</span>

                                                    <FormControl size="small" className="dashboard-content-select-page">
                                                        <Select
                                                            id="select-page"
                                                            value={limitItem.toString()}
                                                            onChange={handleSelectPageChange}
                                                        >
                                                            {
                                                                mediaPageList.map(value => (
                                                                    <MenuItem key={value} value={value}>{value}</MenuItem>
                                                                ))
                                                            }
                                                        </Select>
                                                    </FormControl>

                                                    <span>รายการ</span>
                                                </div>

                                                <Button
                                                    variant="text"
                                                    startIcon={<KeyboardArrowLeftIcon />}
                                                    className="dashboard-content-item-pagination me-4"
                                                    disabled={pagination.page === 1}
                                                    onClick={() => handlePaginationChange("previous")}
                                                >
                                                    ก่อนหน้า
                                                </Button>

                                                <Button
                                                    variant="text"
                                                    endIcon={<KeyboardArrowRightIcon />}
                                                    className="dashboard-content-item-pagination"
                                                    disabled={pagination.page >= pagination.totalPage}
                                                    onClick={() => handlePaginationChange("next")}
                                                >
                                                    ถัดไป
                                                </Button>
                                            </div>
                                        </div> :
                                        <>
                                            <div className="dashboard-content-empty-control">
                                                <img src={UPLOAD_ICON} className="dashboard-content-empty-icon"></img>
                                                <span className="dashboard-content-empty-title">ยังไม่มีรายการอัปโหลดวิดีโอ/ไฟล์เสียง/Infographic</span>
                                                <span className="dashboard-content-empty-desc">กรุณาเริ่มอัปโหลดวิดีโอ เพื่อสร้าง Infographic</span>
                                                <Button
                                                    variant="contained"
                                                    className="dashboard-content-empty-upload-button"
                                                    onClick={handleUploadOpen}
                                                >
                                                    อัปโหลดวิดีโอ
                                                </Button>
                                            </div>
                                        </>
                        }
                    </div>
                </div>
            </div>

            {
                isShowUploadModal &&
                <UploadVideoModal show={isShowUploadModal} onClose={handleUploadClose}></UploadVideoModal>
            }

            {
                isShowSearchModal &&
                <SearchUploadByModal show={isShowSearchModal} userList={userList} selectedUploadBy={filters.selectedUploadBy} onClose={handleCloseSearchModal} />
            }

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    )
}

export default DashboardComponent;